import {Component, Injectable, OnInit, Optional} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@node_modules/@angular/router';
import {ImageCroppedEvent} from '@node_modules/ngx-image-cropper';
import {SchoolService} from '@services/school/school.service';
import {AccountService} from '@services/account/account.service';
import {Store} from '@ngrx/store';
import * as AuthReducer from '@store/auth/reducers';
import {MatDialog, MatDialogRef} from '@angular/material';
import {Subject} from 'rxjs';
import {ViewChild} from '@node_modules/@angular/core';
import {GooglePlaceDirective} from '@node_modules/ngx-google-places-autocomplete';
import {BehaviorSubject} from 'rxjs/internal/BehaviorSubject';
import {UpdateAuthUser} from '@store/auth/actions';
import {User} from '@models/user';
import {PlatformBillingPreviewService} from '@services/platform-billing-preview/platform-billing-preview.service';
import {PlatformBillingPlanAssignmentPlan} from '@models/platform-billing-preview';

@Component({
  selector: 'app-create-school',
  templateUrl: './create-school.component.html',
  styleUrls: ['./create-school.component.scss']
})
@Injectable()
export class CreateSchoolComponent implements OnInit {
  @ViewChild('placesRef') placesRef: GooglePlaceDirective;
  setSchools: BehaviorSubject<User[]> = new BehaviorSubject(null);

  crateSchoolForm: FormGroup;
  subscribes = [];
  user: User;
  admins: [];
  tmpAddress: {
    address: string,
    locality: string,
    administrative_area_level_1: string,
    postal_code: string,
    country: string
  } = {
    address: null,
    locality: null,
    administrative_area_level_1: null,
    postal_code: null,
    country: null
  };
  mask$: Subject<string> = new BehaviorSubject(null);
  currentPhoneFormat;
  currentCountryCode;
  newAdminMask$: Subject<string> = new BehaviorSubject(null);
  newAdminPhoneFormat;
  newAdminCountryCode;
  addressDisabled: boolean;
  imageChangedEvent: any = '';
  logoChangedEvent: any = '';
  croppedLogo: any = '';
  errors = [];
  onFileChangedType = 'avatar';

  availablePlans: PlatformBillingPlanAssignmentPlan[] = [];
  plansLoading = false;
  plansLoadError = false;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private schoolService: SchoolService,
    private accountService: AccountService,
    private platformBillingPreviewService: PlatformBillingPreviewService,
    private authStore: Store<AuthReducer.AuthState>,
    @Optional() public dialogRef: MatDialogRef<CreateSchoolComponent>
  ) { }

  ngOnInit() {
    this.subscribes.push(this.authStore.subscribe(
      (val) => {
        if (val.auth && val.auth.user) {
          this.user = val.auth.user;
        }
      }
    ));

    this.accountService.findUserByRole({role: 'admin'}).subscribe(
      (resp: {data: any}) => {
        this.admins = resp.data;
      }
    );

    this.crateSchoolForm = new FormGroup({
      'name': new FormControl(null, [Validators.required]),
      'user_id': new FormControl(''),
      'description': new FormControl(null),
      'address': new FormControl(null),
      'locality': new FormControl(null),
      'administrative_area_level_1': new FormControl(null),
      'postal_code': new FormControl(null),
      'country': new FormControl(null),
      'phone': new FormControl(null, [Validators.required]),
      'website_url': new FormControl(null),
      'email': new FormControl(null),
      'facebook_url': new FormControl(null),
      'instagram_url': new FormControl(null),
      'notes': new FormControl(null),
      'terms': new FormControl(null),
      'is_try_before_buy': new FormControl(null),
      'hide_payments_tab_during_on_boarding': new FormControl(null),
      'collect_membership_payments_through_mkc': new FormControl(1),
      'platform_plan_id': new FormControl(''),
      'admin_mode': new FormControl('existing'),
      'new_admin_first_name': new FormControl(null),
      'new_admin_last_name': new FormControl(null),
      'new_admin_email': new FormControl(null),
      'new_admin_phone': new FormControl(null),
    });

    if (this.user && this.user.role === 'super_admin') {
      this.loadAvailablePlans();
    }
  }

  loadAvailablePlans() {
    this.plansLoading = true;
    this.plansLoadError = false;
    this.platformBillingPreviewService.getAvailablePlansForNewSchool().subscribe(
      (resp: {data: {available_plans?: PlatformBillingPlanAssignmentPlan[]}}) => {
        this.availablePlans = (resp && resp.data && resp.data.available_plans) || [];
        this.plansLoading = false;
      },
      () => {
        this.plansLoading = false;
        this.plansLoadError = true;
      }
    );
  }

  setAdminMode(mode: 'existing' | 'new') {
    this.crateSchoolForm.get('admin_mode').setValue(mode);
    this.errors['user_id'] = null;
    this.errors['new_admin_email'] = null;
  }

  create() {

    this.errors['name'] = null;
    this.errors['school_owner'] = null;
    this.errors['email'] = null;
    this.errors['description'] = null;
    this.errors['address'] = null;
    this.errors['new_admin_first_name'] = null;
    this.errors['new_admin_last_name'] = null;
    this.errors['new_admin_email'] = null;
    this.errors['new_admin_phone'] = null;
    this.errors['platform_plan_id'] = null;
    this.errors['submit'] = null;

    const isSuperAdmin = this.user.role === 'super_admin';
    const adminMode = this.crateSchoolForm.get('admin_mode').value;

    if (this.crateSchoolForm.get('name').value == null) {
      this.errors['name'] = 'School name is required';
      return;
    }

    if (isSuperAdmin && adminMode === 'new') {
      if (!this.crateSchoolForm.get('new_admin_first_name').value) {
        this.errors['new_admin_first_name'] = 'First name is required';
        return;
      }
      if (!this.crateSchoolForm.get('new_admin_last_name').value) {
        this.errors['new_admin_last_name'] = 'Last name is required';
        return;
      }
      if (!this.crateSchoolForm.get('new_admin_email').value || !this.validateEmail(this.crateSchoolForm.get('new_admin_email').value)) {
        this.errors['new_admin_email'] = 'A valid Admin email is required';
        return;
      }
      if (!this.crateSchoolForm.get('new_admin_phone').value) {
        this.errors['new_admin_phone'] = 'Admin phone is required';
        return;
      }
    } else if (isSuperAdmin && this.isDropdownCtaValue(this.crateSchoolForm.get('user_id').value)) {
      this.errors['user_id'] = 'School Admin is required';
      return;
    }

    if (isSuperAdmin && this.isDropdownCtaValue(this.crateSchoolForm.get('platform_plan_id').value)) {
      this.errors['platform_plan_id'] = 'MKC Platform plan is required';
      return;
    }

    if (this.crateSchoolForm.get('email').value == null) {
      this.errors['email'] = 'Email is required';
      return;
    }else if (!this.validateEmail(this.crateSchoolForm.get('email').value)) {
      this.errors['email'] = 'Wrong email';
      return;
    }

    if (this.crateSchoolForm.get('address').value == null) {
      this.errors['address'] = 'Address is required';
      return;
    }

    if (this.crateSchoolForm.get('description').value == null) {
      this.errors['description'] = 'Description is required';
      return;
    }

    const payload: any = {
      name: this.crateSchoolForm.get('name').value,
      logo: this.croppedLogo,
      description: this.crateSchoolForm.get('description').value,
      owner_id: this.crateSchoolForm.get('user_id').value,
      address_line_1: this.crateSchoolForm.get('address').value,
      locality: this.crateSchoolForm.get('locality').value,
      administrative_area_level_1: this.crateSchoolForm.get('administrative_area_level_1').value,
      postal_code: this.crateSchoolForm.get('postal_code').value,
      country: this.crateSchoolForm.get('country').value,
      phone: '+' + this.currentCountryCode + this.crateSchoolForm.get('phone').value,
      email: this.crateSchoolForm.get('email').value,
      website_url: this.crateSchoolForm.get('website_url').value,
      facebook_url: this.crateSchoolForm.get('facebook_url').value,
      instagram_url: this.crateSchoolForm.get('instagram_url').value,
      notes: this.crateSchoolForm.get('notes').value,
      is_try_before_buy: this.crateSchoolForm.get('is_try_before_buy').value,
      hide_payments_tab_during_on_boarding: this.crateSchoolForm.get('hide_payments_tab_during_on_boarding').value,
      collect_membership_payments_through_mkc: this.crateSchoolForm.get('collect_membership_payments_through_mkc').value ? 1 : 0,
    };

    if (isSuperAdmin) {
      const selectedPlan = this.availablePlans.find(
        plan => plan.plan_id === this.crateSchoolForm.get('platform_plan_id').value
      );
      payload.platform_plan_id = this.crateSchoolForm.get('platform_plan_id').value;
      payload.platform_plan_version_id = selectedPlan && selectedPlan.current_version
        ? selectedPlan.current_version.plan_version_id
        : undefined;
      payload.admin_mode = adminMode;

      if (adminMode === 'new') {
        payload.new_admin_first_name = this.crateSchoolForm.get('new_admin_first_name').value;
        payload.new_admin_last_name = this.crateSchoolForm.get('new_admin_last_name').value;
        payload.new_admin_email = this.crateSchoolForm.get('new_admin_email').value;
        payload.new_admin_phone = this.crateSchoolForm.get('new_admin_phone').value;
        payload.new_admin_country_code = this.newAdminCountryCode;
      }
    }

    this.schoolService.create(payload).subscribe(
      (resp: {data: {}}) => {
        this.authStore.dispatch(new UpdateAuthUser({schools: resp.data}));
        this.closeOrNavigate(true);
      },
      (error) => {
        this.errors['submit'] = (error && error.error && error.error.message) || 'Unable to create School. Please check the details and try again.';
      }
    );
  }

  handleAddressChange(event) {
    // console.log(event);
    if ( Object.keys(event).every(item => {
      return event[item].length > 0;
    })) {
      let address: any;
      address = event.address_line_1 ? event.address_line_1 : event.administrative_area_level_1 ? event.administrative_area_level_1 : event.country;
      this.tmpAddress.address = event.address_line_1;
      this.tmpAddress.locality = event.locality;
      this.tmpAddress.administrative_area_level_1 = event.administrative_area_level_1;
      this.tmpAddress.postal_code = event.postal_code;
      this.tmpAddress.country = event.country;

      this.crateSchoolForm.get('address').setValue(address);
      this.crateSchoolForm.get('locality').setValue(event.locality);
      this.crateSchoolForm.get('administrative_area_level_1').setValue(event.administrative_area_level_1);
      this.crateSchoolForm.get('postal_code').setValue(event.postal_code);
      this.crateSchoolForm.get('country').setValue(event.country);
    } else {
      this.tmpAddress.address = '';
      this.tmpAddress.locality = '';
      this.tmpAddress.administrative_area_level_1 = '';
      this.tmpAddress.postal_code = '';
      this.tmpAddress.country = '';
    }
  }

  onPhoneError(event) {
    this.crateSchoolForm.get('phone').setErrors({'range': true});
  }

  onCountrychange(event) {
    this.currentCountryCode = event.countryData.dialCode;
    this.currentPhoneFormat = event.placeholder;
    this.mask$.next(event.mask.replace(/[0-9]/g, "0"));
  }

  onNewAdminPhoneError(event) {
    this.crateSchoolForm.get('new_admin_phone').setErrors({'range': true});
  }

  onNewAdminCountrychange(event) {
    this.newAdminCountryCode = event.countryData.dialCode;
    this.newAdminPhoneFormat = event.placeholder;
    this.newAdminMask$.next(event.mask.replace(/[0-9]/g, "0"));
  }

  validateEmail(email) {
    const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  }

  onFileChanged(event, logo = false) {

    this.onFileChangedType = logo ? 'logo' : 'avatar';
    console.log(event);
    const types = [
      'image/jpg',
      'image/jpeg',
      'image/png',
    ];
    this.errors[logo ? 'logo' : 'avatar'] = null;
    // this.errors['avatar'] = null;
    const file = event.target.files[0];
    if (file.size > 1000000) {
      this.errors[logo ? 'logo' : 'avatar'] = 'Large Image';
    } else if (!types.includes(file.type)) {
      this.errors[logo ? 'logo' : 'avatar'] = 'Wrong image format';
    } else {
      logo ? this.logoChangedEvent = event : this.imageChangedEvent = event;
    }
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedLogo = event.base64;
  }
  imageLoaded() {
    // show cropper
  }
  loadImageFailed() {
    // show message
  }

  finishCrop(){
    this.logoChangedEvent = null;
    return;
  }

  genCancel(){
    this.closeOrNavigate();
  }

  private closeOrNavigate(result?: boolean) {
    if (this.dialogRef) {
      this.dialogRef.close(result);
      return;
    }

    this.dialog.closeAll();
    this.router.navigate(['/school']);
  }

  private isDropdownCtaValue(value: any): boolean {
    return value === null || value === undefined || value === '';
  }
}
